import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import LoadingSpinner from './LoadingSpinner';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import ReCAPTCHA from 'react-google-recaptcha';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
const ParkingComplaintForm = () => {
  const [formData, setFormData] = useState({
    category: 'Unauthorized/Wrong Parking',
    description: '',
    photos: [],
    name: '',
    mobileNumber: '',
    confirmInfo: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValidated, setCaptchaValidated] = useState(false);
  const handleCaptchaChange = (value) => {
    if (value) {
      setCaptchaValidated(true);
    } else {
      setCaptchaValidated(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file, i) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7); // compressed base64

          const newPhoto = {
            id: Date.now() + '-' + i,
            name: file.name,
            preview: compressedDataUrl
          };

          setFormData((prev) => ({
            ...prev,
            photos: [...prev.photos, newPhoto]
          }));
        };
      };
      reader.readAsDataURL(file);
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  const handleRemovePhoto = (photoId) => {
    const updatedPhotos = formData.photos.filter(photo => photo.id !== photoId);
    
    const photoToRemove = formData.photos.find(photo => photo.id === photoId);
    if (photoToRemove && photoToRemove.preview) {
      URL.revokeObjectURL(photoToRemove.preview);
    }
    
    setFormData({
      ...formData,
      photos: updatedPhotos
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate reCAPTCHA
    if (!captchaValidated) {
      setSnackbar({
        open: true,
        message: 'Please complete the reCAPTCHA',
        severity: 'error'
      });
      return;
    }
    // Check if required fields are filled
    if (!formData.description || !formData.name || !formData.mobileNumber) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for Firestore
      const photoNames = formData.photos.map(photo => ({
        name: photo.name,
        preview: photo.preview,
      }));
      
      // Add document to Firestore
      const docRef = await addDoc(collection(db, 'parking_complaints'), {
        category: formData.category,
        description: formData.description,
        photoNames: photoNames,
        name: formData.name,
        mobileNumber: formData.mobileNumber,
        confirmInfo: formData.confirmInfo,
        createdAt: serverTimestamp()
      });
      
      console.log('Document written with ID: ', docRef.id);
      setSnackbar({
        open: true,
        message: 'Complaint submitted successfully!',
        severity: 'success'
      });
      
      formData.photos.forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
      
      setFormData({
        category: 'Unauthorized/Wrong Parking',
        description: '',
        photos: [],
        name: '',
        mobileNumber: '',
        confirmInfo: false
      });
    } catch (error) {
      console.error('Error adding document: ', error);
      setSnackbar({
        open: true,
        message: 'Error submitting complaint. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mb-5">
      <div className="text-center mb-4">
        <img
          src="/logo.png"
          alt="Parking Illustration"
          className="img-fluid rounded"
          style={{ maxHeight: '200px' }}
        />
      </div>
      {isSubmitting && <LoadingSpinner />}
      <div className="card shadow">
        <div className="card-body p-4">
          <h2 className="card-title mb-4">Parking Complaint</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="category" className="form-label">Complaint Category</label>
              <select 
                id="category" 
                name="category" 
                className="form-select" 
                value={formData.category}
                onChange={handleChange}
              >
                <option value="⁠Unauthorized / Wrong Parking">⁠Unauthorized / Wrong Parking</option>
                <option value="Payment Issue">Payment Issue</option>
                <option value="QR not working"> QR not working</option>
                <option value="App or Booking Issue">App or Booking Issue</option>
                <option value="Enforcement / Fine Dispute">Enforcement / Fine Dispute</option>
                <option value="Staff Behavior">Staff Behavior</option>
                <option value="Infrastructure Issue">Infrastructure Issue</option>
                <option value="⁠Extra Charges">⁠Extra Charges</option>
              </select>
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="form-label">Description</label>
              <textarea 
                id="description" 
                name="description" 
                className="form-control" 
                rows="4"
                value={formData.description}
                onChange={handleChange}
                required
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="photos" className="form-label">Attach Photos</label>
              <div className="input-group mb-3">
                <input 
                  type="file" 
                  id="photos" 
                  name="photos" 
                  className="form-control" 
                  accept="image/*"
                  onChange={handleImageUpload}
                  multiple
                />
              </div>
              
              {formData.photos.length > 0 && (
                <div className="mt-3">
                  <p className="mb-2">Uploaded Photos:</p>
                  <div className="row g-2">
                    {formData.photos.map((photo) => (
                      <div className="col-6 col-md-4 col-lg-3" key={photo.id}>
                        <div className="position-relative border rounded p-1 h-100">
                          <img 
                            src={photo.preview} 
                            alt="Preview" 
                            className="img-fluid rounded" 
                            style={{ maxHeight: '120px', width: '100%', objectFit: 'cover' }}
                          />
                          <button 
                            type="button" 
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle" 
                            style={{ width: '24px', height: '24px', padding: '0', lineHeight: '1' }}
                            onClick={() => handleRemovePhoto(photo.id)}
                          >
                            ×
                          </button>
                          <small className="d-block text-truncate mt-1" style={{ maxWidth: '100%' }}>
                            {photo.name}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <h5 className="mt-4 mb-3">Contact Information</h5>
            
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Name</label>
              <input 
                type="text" 
                id="name" 
                name="name" 
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="mobileNumber" className="form-label">Mobile Number</label>
              <input 
                type="tel" 
                id="mobileNumber" 
                name="mobileNumber" 
                className="form-control"
                value={formData.mobileNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, mobileNumber: value });
                }}
                maxLength={10}
                pattern="\d{10}"
                required
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Enter 10 digit mobile number"
              />
            </div>
            
            <div className="mb-4 form-check">
              <input 
                type="checkbox" 
                id="confirmInfo" 
                name="confirmInfo" 
                className="form-check-input"
                checked={formData.confirmInfo}
                onChange={handleChange}
                required
              />
              <label className="form-check-label" htmlFor="confirmInfo">
                I confirm that the information provided is correct. I understand false details may lead to complaint rejection.
              </label>
            </div>
            
            <div className="mb-4">
              <ReCAPTCHA
                sitekey="6Lc7bokrAAAAAMbS2Y5D8fz1JA87zAu8A_X3usNw"
                onChange={handleCaptchaChange}
                onExpired={() => setCaptchaValidated(false)}
                onErrored={() => setCaptchaValidated(false)}
              />
            </div>
            {/* 6Lc7bokrAAAAAKCFFaxhSVztCiPogghkIQxu_1Kc */}

            <div className="d-grid">
              <button 
                type="submit" 
                className="btn btn-primary btn-lg"
                disabled={!formData.confirmInfo || !captchaValidated}
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ParkingComplaintForm;